import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Entity } from '@scrambl/shared';
import { withDocuments, WithDocuments } from '@/lib/module-definitions/document-module';
import { ModuleTypeEnum } from '@scrambl/shared';

// Create a document-capable entity type
type DocumentCapableEntity = Entity & {
  moduleType: ModuleTypeEnum.ENTITY;
  moduleId: string;
};

// UI State only
interface EntityState {
  // UI State only - selected entity
  selectedEntity: Entity | null;
  
  // UI Actions
  setSelectedEntity: (entity: Entity | null) => void;
  
  // Helper to convert Entity to DocumentCapableEntity (for document module compatibility)
  getDocumentCapableEntity: (entity: Entity | null) => WithDocuments<DocumentCapableEntity> | null;
  
  // Reset action
  reset: () => void;
}

/**
 * Simplified EntityStore that only manages UI state
 * Server state (fetching, creating, updating, deleting entities) 
 * is managed by TanStack Query hooks (useEntitiesQuery, useEntityMutations)
 */
export const useEntityStore = create<EntityState>()(
  persist(
    (set, get) => {
      // Helper to convert Entity to DocumentCapableEntity
      const toDocumentCapableEntity = (entity: Entity): DocumentCapableEntity => ({
        ...entity,
        moduleType: ModuleTypeEnum.ENTITY,
        moduleId: entity.id
      });

      return {
        selectedEntity: null,
        
        setSelectedEntity: (entity: Entity | null) => {
          set({ selectedEntity: entity });
        },
        
        // Helper for document module compatibility
        getDocumentCapableEntity: (entity: Entity | null) => {
          if (!entity) return null;
          return withDocuments(toDocumentCapableEntity(entity));
        },
        
        reset: () => {
          set({
            selectedEntity: null,
          });
        },
      };
    },
    {
      name: 'entity-storage',
      partialize: (state) => ({
        selectedEntity: state.selectedEntity,
      })
    }
  )
);
